import type { Font, Theme } from '@emotion/react';

const fontWeightConversions: Record<
  Weights,
  100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900
> = {
  100: 100,
  lighter: 100,
  200: 200,
  300: 300,
  normal: 400,
  400: 400,
  500: 500,
  600: 600,
  700: 700,
  bold: 700,
  800: 800,
  bolder: 900,
  900: 900,
};

type Weights =
  | 'normal'
  | 'bold'
  | 'bolder'
  | 'lighter'
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900;

function convertFontWeight(weight: Weights): number | string | undefined {
  return Object.keys(fontWeightConversions).includes(`${weight}`)
    ? fontWeightConversions[weight]
    : weight;
}

function googleFontQuery(
  fontsObjects: Partial<Theme['fonts']>
): string[] | null {
  const fonts = Object.values(fontsObjects);

  const distinctFontFamilies = [
    ...Array.from(
      new Set(
        fonts
          .filter((f: Font | undefined): f is Font => Boolean(f?.fontFamily))
          .map((f: Font): string => f.fontFamily)
      )
    ),
  ];

  const filteredFamilies = distinctFontFamilies.filter(
    (fam: string): boolean => {
      return ![
        'Arial',
        'Courier',
        'Georgia',
        'Lucida Sans',
        'Times New Roman',
        'Trebuchet MS',
      ].includes(fam);
    }
  );

  // If no google font's return null
  if (filteredFamilies.length === 0) {
    return null;
  }

  const fontWeights = filteredFamilies.map((family: string): string => {
    const weights: Weights[] = fonts
      .filter(
        (font: Font | undefined): font is Font => font?.fontFamily === family
      )
      .map((font: Font): Weights => font.fontWeight)
      .map(convertFontWeight)
      .filter(
        (val: string | number | undefined): val is Weights =>
          typeof val !== 'undefined'
      ) // filter out empty
      .filter((w: Weights): boolean => typeof w === 'number'); // filter out nonnumerical

    // always include the normal '400' font weight
    const weightString = [...Array.from(new Set([...weights, 400]))]
      .sort()
      .join(';');

    return `${family}:wght@${weightString}`;
  });

  const query = fontWeights.map((s: string): string => {
    return s.replace(/\s/g, '+').replace(/%20/g, '+').replace(/'/g, '');
  });

  const lastIndex = query.length - 1;

  query[lastIndex] = `${query[lastIndex]}&display=swap`;

  return query;
}

export default googleFontQuery;
